import type { MessageContext } from 'vue-i18n';
import { InfoFindingCode } from './types';

export default {
  en: {
    [InfoFindingCode.INFO_SPF_HARD_FAIL]: {
      finding: "Uses '-all' instead of recommended '~all'",
      explanation:
        'The domain uses a strict rejection policy ("-all") for unauthorised senders. While secure, this may block legitimate emails if the SPF record is not carefully maintained. The domains related are:',
    },
    [InfoFindingCode.INFO_CSP_DEPENDENCIES]: {
      finding: 'CSP has {n} dependency | CSP has {n} dependencies',
      explanation:
        'The detected Content Security Policy (CSP) includes various external dependencies. These define which resources the website allows for scripts, styles, and other content, helping to mitigate security risks like XSS attacks. Review the listed dependencies to ensure they align with your security policies.',
    },
    [InfoFindingCode.INFO_CHAIN_LIMIT_EXCEEDED]: {
      finding: 'Redirect limit exceeded',
      explanation:
        'The resource accessed has too many redirects, exceeding the allowed limit. This may be due to a misconfiguration or an infinite redirect loop.',
      redirectionChainTitle: 'The redirected domains are:',
    },
    [InfoFindingCode.INFO_CHAIN_TLD_CHANGED]: {
      finding: 'Top level domain changed in redirection',
      explanation:
        'The resource has been redirected to a different top level domain. . This may indicate a domain migration, misconfiguration, or a potential security concern.',
      redirectionChainTitle: 'The redirected domains are:',
    },
    [InfoFindingCode.INFO_HTTP_TIMEOUT]: {
      finding: 'No timely response during HTTP request',
      explanation:
        'The server took too long to respond to the request. This may be due to high server load, network issues, or the server being down.',
    },
    [InfoFindingCode.INFO_DNS_TIMEOUT]: {
      finding: 'No timely response during DNS lookup',
      explanation:
        'The DNS resolver did not respond within the expected time. This could be caused by network issues, an unresponsive DNS server, or misconfigured DNS settings.',
    },
    [InfoFindingCode.INFO_DNS_FAIL]: {
      finding: 'DNS lookup failed',
      explanation:
        'We were unable to resolve the asset due to a DNS server issue. The DNS server responded with an error, indicating that it could not  provide a valid response. This could be caused by a misconfigured domain, a problem with the authoritative nameservers, or a temporary issue with the DNS infrastructure.',
    },
    [InfoFindingCode.INFO_HOST_NOT_FOUND]: {
      finding: 'Domain does not exist',
      explanation:
        'The domain that is scanned does not exist in the DNS system. This could be due to a typo, an unregistered domain, or a DNS configuration issue.',
    },
    [InfoFindingCode.INFO_HTTP_5XX]: {
      finding: 'Server Error (5XX)',
      explanation:
        'The server responded with a 5XX error indicating it encountered an issue processing the request.',
      receivedTitle: 'Received',
      received: '{statusCode} {statusText}',
      explanationTitle: 'Explanation',
      explanations: {
        500: "This indicates an unexpected condition was encountered that prevented the server from fulfilling the request. It's often caused by unhandled exceptions, misconfigurations, or runtime errors in the application.",
        501: 'The server does not support the functionality required to fulfill the request. This may be due to unsupported HTTP methods or missing server-side logic.',
        502: 'The server, while acting as a gateway or proxy, received an invalid response from an upstream server. This usually indicates issues in inter-service communication or upstream service failure.',
        503: 'The server is currently unable to handle the request due to temporary overloading or scheduled maintenance. This is often a transient issue, but for recurring instances, please investigate the assets added',
        504: 'The server, while acting as a gateway or proxy, did not receive a timely response from an upstream server. This may indicate latency or network issues between services.',
        505: 'The server does not support the HTTP protocol version used in the request. This may be caused by outdated or misconfigured clients.',
        custom:
          'This is a non-standard or custom server error code. It typically represents an internal failure specific to the application or infrastructure.',
      },
    },
    [InfoFindingCode.INFO_CONN_REFUSED]: {
      finding: 'Connection refused on {connectionType} port {port}',
      explanation:
        'The system attempted to connect via {connectionType} to {value}:{port}, but the connection was refused. This usually means that the target is reachable, but no service is listening on the specified port or the connection was actively rejected by the host or firewall.',
    },
    [InfoFindingCode.INFO_CONN_TIMEOUT]: {
      finding: 'Connection timed out on {connectionType} port {port}',
      explanation:
        'The system attempted to connect via {connectionType} to {value}:{port}, but the connection timed out. This suggests that the server is unreachable or unresponsive. A firewall may be silently dropping packets, or the target host may be down or overloaded.',
    },
    [InfoFindingCode.INFO_NO_ROUTE_TO_HOST]: {
      finding: 'No route to host for {connection_type} port {port}',
      explanation:
        'The system was unable to reach {value}:{port} via {connectionType} due to a routing issue. This typically means the host is offline, misconfigured, or not reachable from the scanning environment. Potential causes of this could be the host being offline, misconfigured, or unreachable. Please check the network setup or if there is a missing route.',
    },
    [InfoFindingCode.INFO_TLS_HANDSHAKE_ERROR]: {
      finding: 'TLS handshake error',
      explanation:
        'The system was unable to establish a secure TLS connection. This could be due to server misconfiguration, a firewall or security solution blocking non-browser TLS handshakes, or an incompatible TLS implementation. While browsers may be able to connect successfully, automated security scanners cannot validate the TLS configuration. Consider adding both the root domain and subdomains as separate assets to ensure complete coverage.',
    },
    [InfoFindingCode.INFO_HSTS_HEADER_MALFORMED]: {
      finding: 'Malformed HSTS header',
      explanation:
        'The server responded with a Strict-Transport-Security (HSTS) header that could not be parsed due to an invalid format. HSTS headers must follow a specific structure defined by the RFC 6797 standard, consisting of directives separated by semicolons. Each directive should be well-formed, and unexpected syntax—such as incorrect use of = signs or invalid characters—can prevent browsers and security tools from recognizing the header. As a result, the intended protections offered by HSTS may not be enforced. This weakens the server’s defense against downgrade attacks and other transport-level threats.',
    },
    [InfoFindingCode.INFO_SCAN_TIMEOUT]: {
      finding: 'Scan timed out',
      explanation:
        'The scan did not complete. This could be due to network latency, server responsiveness issues, or an unusually large number of resources to scan.',
    },
    RemoteAccessPorts: {
      [InfoFindingCode.INFO_PORT_EXPOSED]: {
        finding: '{service} port exposed',
        explanation:
          'Port {port} is used for {service}, which is used in remote access services. There is no issue here if this asset is used for remote access services, and please review our Remote Access scans for this asset to assess how securely it has been configured.',
      },
    },
    DatabasePorts: {
      [InfoFindingCode.INFO_PORT_EXPOSED]: {
        finding: '{service} port exposed',
        explanation:
          'Port {port} is used for {service}, which is used in database services. There is no issue here if this asset is used for database services, and please review our Database scans for this asset to assess how securely it has been configured.',
      },
    },
    WebServicePorts: {
      [InfoFindingCode.INFO_PORT_EXPOSED]: {
        finding: '{service} port exposed',
        explanation: ({ named, values }: MessageContext) => {
          const init = `Port ${named('port')} is used for ${named('service')}, and this port needs to be open in order to serve web content.`;
          const http = `${named('service')} is insecure and best practice is that it should be used to redirect to HTTPS rather than serving web content itself.`;
          if (values.service === 'HTTP') {
            return `${init} ${http}`;
          }
          return init;
        },
      },
    },
    FileSharingPorts: {
      [InfoFindingCode.INFO_PORT_EXPOSED]: {
        finding: '{service} port exposed',
        explanation:
          'Port {port} is used for {service}, which is used in file sharing. There is no issue here if this asset is used for file sharing services, and please review our File Sharing scans for this asset to assess how securely it has been configured.',
      },
    },
    EmailPorts: {
      [InfoFindingCode.INFO_PORT_EXPOSED]: {
        finding: '{service} port exposed',
        explanation:
          'Port {port} is used for {service}, which is used in email management. There is no issue here if this asset is used for email services, and please review our Email Security scans for this asset to assess how securely it has been configured.',
      },
    },
    InfrastructurePorts: {
      [InfoFindingCode.INFO_PORT_EXPOSED]: {
        finding: '{service} port exposed',
        explanation:
          'Port {port} is used for {service}, which is used in infrastructure services. There is no issue here if this asset is used for infrastructure services, and please review our Infrastructure scans for this asset to assess how securely it has been configured.',
      },
    },
    recordType: 'Record type',
    [InfoFindingCode.INFO_PORT_WAF]: {
      finding: '{service} port protected by web application firewall (WAF)',
      explanation:
        'A WAF named {waf} has been detected protecting this port. This helps to filter, monitor, and block HTTP traffic to and from a web application, providing an additional layer of security against common web exploits and attacks. While the port is exposed to the internet, the presence of a WAF reduces the risk of exploitation by blocking malicious traffic. However, it is still important to ensure the WAF is properly configured and kept up to date.',
    },
  },
};
